import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "../../../../Common/Components/BaseComponents/Form";
import { InputField } from "../../../../Common/Components/BaseComponents/Input";
import { SelectField } from "../../../../Common/Components/BaseComponents/Select";
import { CreateLeadRequest, Lead, LeadSource, WorkspaceType } from "shared-types";
import { useNavigate } from "react-router-dom";
import { showAlert } from "../../../../Common/Components/BaseComponents/ShowAlert";
import { useLeadsStore } from "../../../../Stores/LeadAndCustomer/leadsStore";
import { Save, Send } from "lucide-react";

const workspaceTypeOptions = [
  { value: WorkspaceType.PRIVATE_ROOM1, label: 'חדר פרטי' },
  { value: WorkspaceType.PRIVATE_ROOM2, label: 'חדר של שתיים' },
  { value: WorkspaceType.PRIVATE_ROOM3, label: 'חדר של שלוש' },
  { value: WorkspaceType.DESK_IN_ROOM, label: 'שולחן בחדר' },
  { value: WorkspaceType.OPEN_SPACE, label: 'אופן ספייס' },
  { value: WorkspaceType.KLIKAH_CARD, label: 'כרטיס קליקה' },
];

const leadSourceOptions = [
  { value: LeadSource.WEBSITE, label: 'אתר אינטרנט' },
  { value: LeadSource.REFERRAL, label: 'הפניה' },
  { value: LeadSource.SOCIAL_MEDIA, label: 'רשתות חברתיות' },
  { value: LeadSource.EVENT, label: 'אירוע' },
  { value: LeadSource.PHONE, label: 'טלפון' },
  { value: LeadSource.WALK_IN, label: 'הגעה פיזית' },
  { value: LeadSource.EMAIL, label: 'אימייל' },
  { value: LeadSource.OTHER, label: 'אחר' },
];

// סכימת zod
const schema = z.object({
  name: z.string().nonempty("חובה למלא שם"),
  phone: z.string().regex(/^0\d{9}$/, "מספר טלפון לא תקין"),
  email: z.string().email("אימייל לא תקין"),
  businessType: z.string().min(2, "מינימום 2 תווים").refine(val =>
    !(/([a-zA-Z].*[א-ת])|([א-ת].*[a-zA-Z])/.test(val)), "אין לערבב עברית ואנגלית"),
  interestedIn: z.nativeEnum(WorkspaceType),
  source: z.nativeEnum(LeadSource).refine(val => !!val, { message: "חובה למלא מקור פנייה" }),
  followUpDate: z.string().optional(),
  notes: z.string().optional(),
});

type LeadFormData = z.infer<typeof schema>;

interface LeadFormProps {
  onModalClose?: () => void;
}

export const LeadForm = ({ onModalClose }: LeadFormProps) => {
  const methods = useForm<LeadFormData>({
    resolver: zodResolver(schema),
    mode: "onChange",
  });
  const navigate = useNavigate();
  const { handleCreateLead, loading } = useLeadsStore();
  const { reset } = methods;

  const onSubmit = async (data: LeadFormData) => {
    try {
      const leadRequest: CreateLeadRequest = {
        name: data.name,
        phone: data.phone,
        email: data.email,
        businessType: data.businessType,
        interestedIn: data.interestedIn,
        source: data.source,
        followUpDate: data.followUpDate,
        notes: data.notes,
      };

      const lead: Lead | undefined = await handleCreateLead(leadRequest);
      let latestError = useLeadsStore.getState().error;
      if (latestError) {
        showAlert("שגיאה ביצירת מתעניין", latestError, "error");
        return;
      }
      console.log("הנתונים נשלחו בהצלחה:", lead);
      reset();
      showAlert("", "המתעניין נוסף בהצלחה", "success");
      
      if (onModalClose) {
        onModalClose();
      } else {
        navigate(-1);
      }
    } catch (error: Error | any) {
      console.error("שגיאה בשליחת הטופס:", error);
      showAlert("שגיאה ביצירת מתעניין", error.message || "שגיאה בלתי צפויה", "error");
    }
  };

  return (
    <div className={onModalClose ? "" : "max-w-2xl mx-auto p-4"}>
      {!onModalClose && <h2 className="text-xl font-bold text-center text-blue-600 mb-4">טופס יצירת ליד</h2>}

      <Form
        label={onModalClose ? "" : "פרטי ליד"}
        schema={schema}
        onSubmit={onSubmit}
        methods={methods}
      >
        <InputField name="name" label="שם" required />
        <InputField name="phone" label="טלפון" required />
        <InputField name="email" label="אימייל" required />
        <InputField name="businessType" label="סוג עסק" required />
        <SelectField
          name="interestedIn"
          label="מתעניין ב"
          options={workspaceTypeOptions}
          required
        />

        <SelectField
          name="source"
          label="מקור פנייה"
          options={leadSourceOptions}
          required
        />
        {/* <SelectField
          name="status"
          label="סטטוס"
          options={[
            { value: "new", label: "חדש" },
            { value: "contacted", label: "בוצעה פנייה" },
            { value: "interested", label: "מתעניין" },
            { value: "converted", label: "הומר ללקוח" },
          ]}
        /> */}
        <InputField name="followUpDate" label="תאריך מעקב" type="date" />
        <InputField name="notes" label="הערות" type="textarea" />


        <div className={`${onModalClose ? "col-span-2 flex justify-center" : "flex justify-end"} mt-4`}>
          <button 
            type="submit" 
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition flex items-center gap-2"
          >
            {onModalClose ? <Save size={16} /> : <Send size={16} />}
            {onModalClose ? "שמור" : "שלח"}
          </button>
        </div>
      </Form>
       {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
                </div>
            )}
    </div>
    
  );
};